import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Checkbox, Typography, Tag } from "antd";
import formatDistance from "date-fns/formatDistance";
import { it } from "date-fns/locale";
import DocPreview from "./DocPreview";

const { Text, Paragraph } = Typography;

// Dimensioni di riferimento di un foglio A4 a 96dpi.
const A4_W = 794;
const A4_H = 1123;

// Renderizza il contenuto a dimensione A4 e lo scala per riempire la larghezza della card.
const ScaledDoc = ({ children }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.25);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w) setScale(w / A4_W);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ width: "100%", aspectRatio: `${A4_W} / ${A4_H}`, overflow: "hidden", background: "#fff", position: "relative" }}
    >
      <div
        style={{ width: A4_W, height: A4_H, transform: `scale(${scale})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0 }}
      >
        {children}
      </div>
    </div>
  );
};

// Etichette di stato del documento. Il flag "Precompilato" è omesso di proposito:
// è di fatto sempre presente, quindi non porta informazione utile.
const docFlags = (doc) => {
  const flags = [];
  if (doc.generico) flags.push("Generico");
  return flags;
};

// Card documento: header (titolo + eventuale selezione), anteprima a foglio (opzionale), footer.
const DocumentCard = ({
  doc,
  selected,
  onToggleSelect,
  onOpen,
  showPreview = true,
  selectable = true,
  showOrgLink = true,
  showFlags = false,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  // Monta l'anteprima solo quando la card entra nel viewport (e solo se abilitata).
  useEffect(() => {
    if (!showPreview || visible) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, showPreview]);

  const previewContent = () => {
    if (!visible) return null;
    if (doc.pdf_form && doc.pdf_form.url) {
      return (
        <object
          data={`${doc.pdf_form.url}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
          type="application/pdf"
          width={A4_W}
          height={A4_H}
        >
          PDF
        </object>
      );
    }
    return (
      <div style={{ width: A4_W, padding: 24 }}>
        <DocPreview id={doc.objectId} />
      </div>
    );
  };

  const openDoc = () => {
    if (onOpen) onOpen(doc);
  };

  const toggleDocSelect = () => {
    if (onToggleSelect) onToggleSelect(doc.objectId);
  };

  const flags = showFlags ? docFlags(doc) : [];

  return (
    <div ref={ref} style={{ height: "100%" }}>
      <Card
        size="small"
        hoverable
        onClick={openDoc}
        style={{
          height: "100%",
          border: selected ? "2px solid #1890ff" : "1px solid #d9d9d9",
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflow: "hidden",
          cursor: "pointer",
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header: titolo (+ checkbox se selezionabile) */}
        <div
          style={{
            padding: "10px 12px",
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            background: selected ? "#e6f4ff" : undefined,
          }}
        >
          {selectable && (
            <Checkbox
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={toggleDocSelect}
              style={{ marginTop: 2 }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Altezza fissa di 2 righe: l'anteprima parte sempre dalla stessa quota,
                così le card restano allineate anche con titoli su una o due righe. */}
            <Paragraph
              strong
              style={{ margin: 0, lineHeight: 1.2, height: "2.4em" }}
              ellipsis={{ rows: 2, tooltip: doc.nome_documento }}
            >
              {doc.nome_documento}
            </Paragraph>
            {/* Area badge ad altezza riservata (anche quando vuota) per non sfalsare l'header. */}
            {showFlags && (
              <div style={{ marginTop: 4, height: 22, display: "flex", gap: 4, overflow: "hidden" }}>
                {flags.map((flag) => (
                  <Tag key={flag} color="blue" style={{ marginInlineEnd: 0 }}>
                    {flag}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Anteprima a foglio (opzionale) — click per ingrandire */}
        {showPreview && (
          <div
            title="Clicca per ingrandire"
            style={{ position: "relative", cursor: "pointer", borderTop: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0" }}
          >
            <div aria-hidden style={{ position: "absolute", inset: 0, zIndex: 2 }} />
            <ScaledDoc>{previewContent()}</ScaledDoc>
          </div>
        )}

        {/* Footer: org (opzionale) + data — click per ingrandire */}
        <div
          style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6, cursor: "pointer" }}
        >
          {showOrgLink && (
            <Text type="secondary" ellipsis style={{ fontSize: 13 }}>
              {doc.org_pointer ? (
                <Link to={`/orgs/${doc.org_pointer.objectId}`} onClick={(e) => e.stopPropagation()}>
                  {doc.org_pointer.nome}
                </Link>
              ) : (
                "❌ nessuna org"
              )}
            </Text>
          )}
          {doc.updatedAt && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatDistance(new Date(doc.updatedAt), new Date(), { locale: it })} fa
            </Text>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DocumentCard;
